import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getExperiencias, getCategorias, type ExperienciaAdmin, type Categoria } from '../lib/experienciasStorage';
import { getApiBase } from '../lib/api';
import { resolveMediaUrl } from '../../../shared/lib/mediaUrl';
import { adminPublicPath } from '../lib/adminPublicPath';

const FALLBACK_NATURE_HERO_URLS = [
  'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1600&q=80',
  'https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=1600&q=80',
  'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1600&q=80',
  'https://images.unsplash.com/photo-1518173946689-a4c8892bbd9f?w=1600&q=80',
];

function categoriaNomeNormalizado(c: Categoria | undefined): string {
  return (c?.name ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function experienciaEhNaturezaPasseio(exp: ExperienciaAdmin, categorias: Categoria[]): boolean {
  if (exp.tipoExperiencia === 'terapia') return false;
  return (exp.categoryIds ?? []).some((id) => {
    const cat = categorias.find((c) => c.id === id);
    return categoriaNomeNormalizado(cat).includes('natureza');
  });
}

function primeiraUrlImagemExperiencia(exp: ExperienciaAdmin): string {
  const seo = exp.imagensSEO?.find((i) => i.url?.trim());
  if (seo?.url?.trim()) {
    const u = resolveMediaUrl(seo.url.trim(), getApiBase());
    if (u && !u.startsWith('data:')) return u;
  }
  for (const raw of exp.imagens ?? []) {
    if (!raw?.trim()) continue;
    const u = resolveMediaUrl(raw.trim(), getApiBase());
    if (u && !u.startsWith('data:')) return u;
  }
  return '';
}

function urlsHeroNaturezaCadastradas(): string[] {
  const categorias = getCategorias();
  const seen = new Set<string>();
  const out: string[] = [];
  for (const exp of getExperiencias()) {
    if (!experienciaEhNaturezaPasseio(exp, categorias)) continue;
    const u = primeiraUrlImagemExperiencia(exp);
    if (u && !seen.has(u)) {
      seen.add(u);
      out.push(u);
    }
  }
  return out;
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [gmailLoading, setGmailLoading] = useState(false);
  const [heroUrl, setHeroUrl] = useState<string>(() => FALLBACK_NATURE_HERO_URLS[0] ?? '');
  const navigate = useNavigate();
  const { user, login, loginWithGmail, isFirebaseReady } = useAuth();

  const pickRandomHero = useCallback((pool: string[]) => {
    if (pool.length === 0) {
      const fb = FALLBACK_NATURE_HERO_URLS;
      setHeroUrl(fb[Math.floor(Math.random() * fb.length)] ?? fb[0] ?? '');
      return;
    }
    setHeroUrl(pool[Math.floor(Math.random() * pool.length)] ?? pool[0] ?? '');
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const pool = urlsHeroNaturezaCadastradas();
    pickRandomHero(pool);
    if (pool.length < 2) return undefined;
    const id = window.setInterval(() => pickRandomHero(pool), 16000);
    return () => clearInterval(id);
  }, [pickRandomHero]);

  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const result = await login(email.trim().toLowerCase(), password);
    if (result.ok) navigate('/dashboard', { replace: true });
    else setError(result.error ?? 'Erro ao entrar.');
  };

  const handleGmailLogin = async () => {
    setError('');
    setGmailLoading(true);
    const result = await loginWithGmail();
    setGmailLoading(false);
    if (result.ok) {
      navigate('/dashboard', { replace: true });
    } else if (result.error) {
      setError(result.error);
    }
  };

  if (user) {
    return (
      <div className="w-full min-h-screen bg-white flex items-center justify-center">
        <p className="text-gray-500">Redirecionando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-white">
      {/* Painel esquerdo: formulário */}
      <div className="flex-1 flex flex-col justify-center px-6 py-10 md:px-12 lg:px-16">
        <div className="w-full max-w-md mx-auto">
          <div className="mb-8">
            <img
              src={adminPublicPath('/Navi-Experience-Logo.png')}
              alt="Navi Experience"
              className="h-14 sm:h-16 w-auto max-w-[min(100%,280px)] object-contain object-left"
              width={280}
              height={64}
            />
          </div>

          <h1 className="text-gray-800 text-2xl font-semibold mb-2">Acesse sua conta</h1>
          <p className="text-gray-600 text-sm mb-6">Área administrativa</p>

          <p className="text-amber-700 text-sm mb-4 bg-amber-50 py-2 px-4 rounded-lg border border-amber-200">
            Prefira usar <strong>e-mail e senha</strong> — é mais estável em ambiente local. O Gmail pode travar em tela branca.
          </p>

          {error && (
            <p className="text-red-600 text-sm mb-4 bg-red-50 py-2 px-4 rounded-lg border border-red-200">
              {error}
            </p>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-gray-700 text-sm font-medium mb-1">
                E-mail
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                className="w-full py-3 px-4 bg-gray-50 text-gray-800 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-gray-700 text-sm font-medium mb-1">
                Senha
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full py-3 px-4 pr-10 bg-gray-50 text-gray-800 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              <div className="mt-2 text-right">
                <Link to="/recuperar-senha" className="text-sm text-orange-600 font-medium hover:underline">
                  Esqueci a senha
                </Link>
              </div>
            </div>

            <button
              type="submit"
              className="w-full py-3 px-6 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all hover:opacity-95"
            >
              Entrar
            </button>
          </form>

          {isFirebaseReady && (
            <>
              <div className="mt-6 flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-gray-500 text-sm">ou continue com</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              <button
                type="button"
                onClick={() => void handleGmailLogin()}
                disabled={gmailLoading}
                className="mt-6 w-full py-3 px-6 rounded-lg border-2 border-gray-200 bg-white text-gray-700 font-medium flex items-center justify-center gap-3 hover:bg-gray-50 hover:border-gray-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {gmailLoading ? (
                  <>Abrindo Google...</>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.18H12v4.16h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continuar com Google
                  </>
                )}
              </button>
              <p className="mt-2 text-xs text-gray-500">
                Se abrir uma aba em branco: feche-a e use e-mail/senha acima.
              </p>
            </>
          )}
        </div>
      </div>

      {/* Painel direito: fotos de experiências Natureza (cadastro) — aleatório e rotação; fallback paisagem */}
      <div className="hidden md:block md:w-[45%] lg:w-1/2 min-h-screen relative bg-gray-900 overflow-hidden">
        {heroUrl ? (
          <img
            key={heroUrl}
            src={heroUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/20 pointer-events-none" aria-hidden />
      </div>
    </div>
  );
}
